import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { PROFIL_STATUS, QUALIFIKATIONEN, BUNDESLAENDER } from '../../lib/constants'
import type { Pflegekraft } from '../../lib/types'
import './AdminPages.css'

export default function PflegekraefteListe() {
  const [profiles, setProfiles] = useState<Pflegekraft[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterBundesland, setFilterBundesland] = useState('')
  const [filterQualifikation, setFilterQualifikation] = useState('')
  const navigate = useNavigate()

  useEffect(() => {
    loadProfiles()
  }, [filterStatus, filterBundesland, filterQualifikation])

  async function loadProfiles() {
    setLoading(true)
    let query = supabase
      .from('pflegekraefte')
      .select('*')
      .order('created_at', { ascending: false })

    if (filterStatus) query = query.eq('status', filterStatus)
    if (filterBundesland) query = query.eq('bundesland', filterBundesland)
    if (filterQualifikation) query = query.eq('qualifikation', filterQualifikation)

    const { data, error } = await query
    if (error) console.error(error)
    setProfiles((data as Pflegekraft[]) || [])
    setLoading(false)
  }

  const filtered = profiles.filter((p) => {
    if (!search) return true
    const s = search.toLowerCase()
    return (
      p.vorname?.toLowerCase().includes(s) ||
      p.nachname?.toLowerCase().includes(s) ||
      p.email?.toLowerCase().includes(s) ||
      p.plz?.includes(s) ||
      p.wohnort?.toLowerCase().includes(s)
    )
  })

  const statusColor = (status: string) =>
    PROFIL_STATUS.find((s) => s.value === status)?.color || '#94A3B8'

  return (
    <div className="admin-page animate-fade-in-up">
      <div className="page-header">
        <div>
          <h1>Pflegekräfte</h1>
          <p className="page-subtitle">{profiles.length} Profile gesamt</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Name, E-Mail, PLZ oder Ort suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
            id="pflegekraefte-search"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
          id="filter-status"
        >
          <option value="">Alle Status</option>
          {PROFIL_STATUS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <select
          value={filterBundesland}
          onChange={(e) => setFilterBundesland(e.target.value)}
          className="filter-select"
          id="filter-bundesland"
        >
          <option value="">Alle Bundesländer</option>
          {BUNDESLAENDER.map((b) => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>

        <select
          value={filterQualifikation}
          onChange={(e) => setFilterQualifikation(e.target.value)}
          className="filter-select"
          id="filter-qualifikation"
        >
          <option value="">Alle Qualifikationen</option>
          {QUALIFIKATIONEN.map((q) => (
            <option key={q.value} value={q.value}>{q.label}</option>
          ))}
        </select>
      </div>

      <div className="table-card">
        {loading ? (
          <div className="table-loading"><div className="loading-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <p>Keine Profile gefunden.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Qualifikation</th>
                  <th>Bereich</th>
                  <th>Bundesland</th>
                  <th>Status</th>
                  <th>Erstellt</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/admin/pflegekraefte/${p.id}`)}>
                    <td className="cell-name">
                      <span>{p.vorname} {p.nachname}</span>
                      <span className="cell-sub">{p.email}</span>
                    </td>
                    <td>{QUALIFIKATIONEN.find((q) => q.value === p.qualifikation)?.label || p.qualifikation || '–'}</td>
                    <td>{p.aktueller_bereich || '–'}</td>
                    <td>{BUNDESLAENDER.find((b) => b.value === p.bundesland)?.label || p.bundesland || '–'}</td>
                    <td>
                      <span className="status-badge" style={{ '--badge-color': statusColor(p.status) } as React.CSSProperties}>
                        {PROFIL_STATUS.find((s) => s.value === p.status)?.label || p.status}
                      </span>
                    </td>
                    <td className="cell-date">{new Date(p.created_at).toLocaleDateString('de-DE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
